import React, { useEffect, useRef, useState } from "react";
import { NIFTY_INDEX, NIFTY50_STOCKS } from "../constants/nifty50";

// Generic searchable dropdown reused by all three dashboards. `options` is
// the plain list to filter/search (stocks, MCX commodities, ...); `indexOption`
// optionally pins one entry (e.g. the NIFTY 50 index) above the searchable
// grid, labeled with `indexLabel`. Both default to the original Option &
// Future dashboard's NIFTY 50 + constituents list for backward compatibility.
export default function UnderlyingSelect({
  value,
  onChange,
  options = NIFTY50_STOCKS,
  indexOption = NIFTY_INDEX,
  indexLabel = "Index",
  placeholder = "Filter...",
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = options.filter((s) => s.toLowerCase().includes(filter.toLowerCase()));

  const pick = (sym) => {
    onChange(sym);
    setOpen(false);
    setFilter("");
  };

  return (
    <div style={{ position: "relative", width: "100%" }} ref={ref}>
      <button type="button" style={styles.trigger} onClick={() => setOpen((o) => !o)}>
        <span>{value}</span>
        <span style={{ color: "var(--text-dim)" }}>▾</span>
      </button>

      {open && (
        <div style={styles.popover}>
          <input
            autoFocus
            placeholder={placeholder}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterInput}
          />

          {indexOption && (
            <div
              style={{ ...styles.optionRow, fontWeight: 600, borderBottom: "1px solid var(--border)" }}
              onClick={() => pick(indexOption)}
            >
              {indexOption} <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>({indexLabel})</span>
            </div>
          )}

          <div style={styles.grid}>
            {filtered.map((sym) => (
              <div key={sym} style={styles.optionCell} onClick={() => pick(sym)}>
                {sym}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ color: "var(--text-dim)", gridColumn: "span 3", padding: "8px 4px" }}>No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  trigger: {
    width: "100%", background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 5,
    color: "var(--text)", padding: "5px 8px", fontSize: 12, fontFamily: "var(--font-mono)",
    display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer",
  },
  popover: {
    position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20,
    width: 360, background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)", padding: 10,
  },
  filterInput: {
    width: "100%", background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 5,
    color: "var(--text)", padding: "6px 8px", fontSize: 12, marginBottom: 8, boxSizing: "border-box",
  },
  optionRow: { padding: "6px 6px", cursor: "pointer", fontSize: 12.5, marginBottom: 6, borderRadius: 4 },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "2px 6px",
    maxHeight: 260, overflowY: "auto",
  },
  optionCell: {
    padding: "6px 6px", cursor: "pointer", fontSize: 12, borderRadius: 4, fontFamily: "var(--font-mono)",
  },
};
